<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading</div>
    <div v-if="loaded" class="root">

      <!-- Scanline overlay -->
      <div class="scanlines" aria-hidden="true"></div>

      <!-- Header -->
      <header class="cy-header">
        <div class="cy-header-inner">
          <div class="cy-brand">
            <img v-if="loinfo && loinfo.main_logo" :src="loinfo.main_logo" class="cy-logo" />
            <div class="cy-status">
              <span class="cy-dot"></span>
              <span class="cy-status-text">SYSTEM ONLINE</span>
            </div>
          </div>
          <div class="cy-ticker">
            <span class="ct-label">LO:</span>
            <span class="ct-val">{{ loName }}</span>
            <span class="ct-sep">//</span>
            <span class="ct-label">PARTNER:</span>
            <span class="ct-val">{{ realtorName }}</span>
          </div>
   
        </div>
        <div class="cy-underline"></div>
      </header>

      <!-- Body -->
      <div class="cy-body">

        <!-- Terminal hero -->
        <section class="cy-hero">
          <div class="terminal-bar">
            <span class="tb-btn tb-red"></span>
            <span class="tb-btn tb-yellow"></span>
            <span class="tb-btn tb-green"></span>
            <span class="tb-title">session.exe</span>
          </div>
          <div class="terminal-content">
            <div class="tc-line">
              <span class="tc-prompt">></span>
              <span class="tc-cmd">init_mortgage_session</span>
            </div>
            <h1 class="cy-h1" v-html="heroH1Html"></h1>
            <p class="cy-body-text">{{ heroBody }}</p>
            <div class="cy-btns">
              <a class="cy-btn-primary" :href="applyRightLink">{{ heroPrimaryText }}</a>
              <a v-if="applyLeftLink" class="cy-btn-ghost" :href="applyLeftLink">{{ heroSecondaryText }}</a>
            </div>
            <div class="cy-contacts">
              <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="cy-ct">
                <span class="cct-key">CALL</span> {{ loinfo.phone_number }}
              </a>
              <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="cy-ct">
                <span class="cct-key">MAIL</span> {{ loinfo.email }}
              </a>
            </div>
          </div>
        </section>

        <!-- Agent cards -->
        <aside class="cy-sidebar">

          <div class="cy-card lo-cy">
            <div class="cyc-header">
              <div class="cyc-id">
                <span class="cyc-prefix">ID_</span>LO
              </div>
              <span class="cyc-ping lo-ping">◉</span>
            </div>
            <div class="cyc-profile">
              <div class="cyc-img-wrap lo-img-wrap">
                <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="cyc-img" />
              </div>
              <div class="cyc-info">
                <div class="cyc-name">{{ loName }}</div>
                <div class="cyc-role">{{ loTitle }}</div>
                <div v-if="loinfo && loinfo.nmls" class="cyc-nmls">{{ (loinfo.nmls || "").trim() }}</div>
              </div>
            </div>
            <div class="cyc-data">
              <div class="cd-row" v-if="loinfo && loinfo.phone_number">
                <span class="cdr-key">PHONE</span>
                <a :href="'tel:' + loinfo.phone_number" class="cdr-val lo-val">{{ loinfo.phone_number }}</a>
              </div>
              <div class="cd-row" v-if="loinfo && loinfo.email">
                <span class="cdr-key">EMAIL</span>
                <a :href="'mailto:' + loinfo.email" class="cdr-val lo-val email-trunc">{{ loinfo.email }}</a>
              </div>
            </div>
           
          </div>

          <div class="cy-card re-cy">
            <div class="cyc-header">
              <div class="cyc-id">
                <span class="cyc-prefix">ID_</span>RE
              </div>
              <span class="cyc-ping re-ping">◉</span>
            </div>
            <div class="cyc-profile">
              <div class="cyc-img-wrap re-img-wrap">
                <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="cyc-img" />
              </div>
              <div class="cyc-info">
                <div class="cyc-name">{{ realtorName }}</div>
                <div class="cyc-role">{{ realtor && realtor.brokerage_name }}</div>
                <div v-if="realtor && realtor.nmls" class="cyc-nmls">NMLS {{ (realtor.nmls || "").trim() }}</div>
              </div>
            </div>
            <div class="cyc-data">
              <div class="cd-row" v-if="realtor && realtor.phone_number">
                <span class="cdr-key">PHONE</span>
                <a :href="'tel:' + realtor.phone_number" class="cdr-val re-val">{{ realtor.phone_number }}</a>
              </div>
              <div class="cd-row" v-if="realtor && realtor.email">
                <span class="cdr-key">EMAIL</span>
                <a :href="'mailto:' + realtor.email" class="cdr-val re-val email-trunc">{{ realtor.email }}</a>
              </div>
              <div class="cd-row" v-if="realtor && realtor.website_url">
                <span class="cdr-key">WEB</span>
                <a :href="realtor.website_url" class="cdr-val re-val" target="_blank" rel="noreferrer">VISIT →</a>
              </div>
            </div>
          </div>

        </aside>
      </div>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending: { primary: "#eb610a", accent: "#646363" },
  radiantmortgage: { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage: { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam: { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage: { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage: { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial: { primary: "#0b1726", accent: "#c69c39" },
};
function getBrandKey(email) {
  if (!email) return null;
  return email.slice(email.indexOf("@") + 1).toLowerCase().split(".")[0] || null;
}
export default {
  name: "CoBrandCypher",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Access your<br/><em>new home.</em>";
      const parts = h.split(",");
      if (parts.length > 1) return `${parts[0].trim()}<br/><em>${parts.slice(1).join(",").trim()}</em>`;
      return h;
    },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule Appointment"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      return [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim()
        || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Exo+2:wght@300;400;500;600;700&display=swap');

.page {
  min-height: 100vh;
  background: #0c0f0c;
  color: #a8c8a0;
  font-family: 'Exo 2', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --neon: #4cff91;
  --neon-dim: rgba(76,255,145,0.6);
  --bg: #0c0f0c;
  --card-bg: #101410;
  --border: rgba(76,255,145,0.12);
  --muted: #6b8c70;
  overflow: hidden;
  position: relative;
}
.root { position: relative; z-index: 1; display: flex; flex-direction: column; min-height: 100vh; }

/* SCANLINES */
.scanlines {
  position: fixed; inset: 0; pointer-events: none; z-index: 100; opacity: 0.03;
  background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(76,255,145,1) 2px, rgba(76,255,145,1) 3px);
}

/* HEADER */
.cy-header { background: #080b08; }
.cy-header-inner { display: flex; align-items: center; justify-content: space-between; padding: 14px 48px; }
.cy-brand { display: flex; align-items: center; gap: 20px; }
.cy-logo { width: 130px; max-height:68px; object-fit: contain; filter: brightness(0) invert(0.6) sepia(1) hue-rotate(80deg) saturate(2); opacity: 0.8; }
.cy-status { display: flex; align-items: center; gap: 6px; }
.cy-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--neon); box-shadow: 0 0 8px var(--neon); animation: blink 2s step-end infinite; }
@keyframes blink { 50% { opacity: 0; } }
.cy-status-text { font-family: 'Share Tech Mono', monospace; font-size: 9px; color: var(--neon-dim); letter-spacing: 0.2em; }
.cy-ticker { display: flex; align-items: center; gap: 8px; font-family: 'Share Tech Mono', monospace; font-size: 10px; }
.ct-label { color: var(--muted); letter-spacing: 0.1em; }
.ct-val { color: #c8e4c0; }
.ct-sep { color: var(--muted); }
.cy-cta { font-family: 'Share Tech Mono', monospace; font-size: 10px; text-decoration: none; background: transparent; color: var(--neon); border: 1px solid var(--neon); padding: 9px 20px; border-radius: 2px; letter-spacing: 0.1em; transition: background 0.2s, color 0.2s; box-shadow: 0 0 12px rgba(76,255,145,0.15); }
.cy-cta:hover { background: var(--neon); color: #0c0f0c; }
.cy-underline { height: 1px; background: linear-gradient(90deg, transparent, var(--neon), transparent); opacity: 0.3; }

/* BODY */
.cy-body { display: grid; grid-template-columns: 1fr 340px; flex: 1; max-width: 1200px; margin: 0 auto; width: 100%; padding: 48px; gap: 40px; align-items: start; }

/* TERMINAL HERO */
.cy-hero { border: 1px solid var(--border); border-radius: 6px; overflow: hidden; background: var(--card-bg); }
.terminal-bar { background: #1a1e1a; padding: 10px 14px; display: flex; align-items: center; gap: 8px; border-bottom: 1px solid var(--border); }
.tb-btn { width: 10px; height: 10px; border-radius: 50%; }
.tb-red { background: #ff5f56; }
.tb-yellow { background: #ffbd2e; }
.tb-green { background: #27c93f; }
.tb-title { font-family: 'Share Tech Mono', monospace; font-size: 10px; color: var(--muted); margin-left: 8px; letter-spacing: 0.1em; }
.terminal-content { padding: 32px 36px; }
.tc-line { display: flex; gap: 8px; align-items: center; margin-bottom: 16px; }
.tc-prompt { font-family: 'Share Tech Mono', monospace; color: var(--neon); font-size: 14px; }
.tc-cmd { font-family: 'Share Tech Mono', monospace; font-size: 11px; color: var(--muted); }
.cy-h1 { font-size: clamp(36px, 5vw, 60px); font-weight: 700; line-height: 1.08; color: #d8f0d0; margin: 0 0 18px; letter-spacing: -0.01em; }
.cy-h1 em { font-style: italic; color: var(--neon); font-weight: 300; text-shadow: 0 0 20px rgba(76,255,145,0.4); }
.cy-body-text { font-size: 14px; line-height: 1.76; color: var(--muted); margin-bottom: 28px; font-weight: 300; }
.cy-btns { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 20px; justify-content: center; }
.cy-btn-primary { font-family: 'Share Tech Mono', monospace; font-size: 10px; text-decoration: none; background: var(--neon); color: #0c0f0c; padding: 12px 24px; border-radius: 2px; letter-spacing: 0.08em; transition: box-shadow 0.2s; width: 55%; text-align: center; }
.cy-btn-primary:hover { box-shadow: 0 0 24px rgba(76,255,145,0.5); }
.cy-btn-ghost { font-family: 'Share Tech Mono', monospace; font-size: 10px; text-decoration: none; color: var(--muted); border: 1px solid var(--border); padding: 12px 20px; border-radius: 2px; transition: border-color 0.18s; }
.cy-btn-ghost:hover { border-color: var(--neon); color: var(--neon); }
.cy-contacts { display: flex; flex-direction: column; gap: 6px; }
.cy-ct { font-family: 'Share Tech Mono', monospace; font-size: 10px; text-decoration: none; color: var(--muted); display: flex; gap: 12px; align-items: center; transition: color 0.18s; }
.cy-ct:hover { color: var(--neon); }
.cct-key { color: var(--neon-dim); letter-spacing: 0.1em; }

/* SIDEBAR */
.cy-sidebar { display: flex; flex-direction: column; gap: 14px; }
.cy-card { background: var(--card-bg); border: 1px solid var(--border); border-radius: 4px; overflow: hidden; }
.cyc-header { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: #1a1e1a; border-bottom: 1px solid var(--border); }
.cyc-id { font-family: 'Share Tech Mono', monospace; font-size: 11px; color: #c8e4c0; }
.cyc-prefix { color: var(--muted); }
.cyc-ping { font-size: 10px; }
.lo-ping { color: var(--neon); text-shadow: 0 0 8px var(--neon); }
.re-ping { color: var(--a); text-shadow: 0 0 8px var(--a); }
.cyc-profile { display: flex; gap: 12px; align-items: flex-start; padding: 14px; }
.cyc-img-wrap { flex-shrink: 0; position: relative; }
.cyc-img { width: 48px; height: 48px; border-radius: 3px; object-fit: cover; display: block; }
.lo-img-wrap::after { content: ""; position: absolute; inset: -2px; border-radius: 4px; border: 1px solid var(--neon); opacity: 0.4; }
.re-img-wrap::after { content: ""; position: absolute; inset: -2px; border-radius: 4px; border: 1px solid var(--a); opacity: 0.4; }
.cyc-name { font-size: 14px; font-weight: 600; color: #d8f0d0; }
.cyc-role { font-size: 10px; color: var(--muted); margin-top: 2px; }
.cyc-nmls { font-family: 'Share Tech Mono', monospace; font-size: 9px; color: var(--muted); opacity: 0.5; margin-top: 2px; }
.cyc-data { padding: 0 14px 12px; display: flex; flex-direction: column; gap: 6px; border-top: 1px solid var(--border); padding-top: 10px; }
.cd-row { display: flex; gap: 10px; }
.cdr-key { font-family: 'Share Tech Mono', monospace; font-size: 8px; color: var(--muted); letter-spacing: 0.12em; width: 38px; flex-shrink: 0; margin-top: 1px; }
.cdr-val { font-family: 'Share Tech Mono', monospace; font-size: 9px; text-decoration: none; color: var(--muted); transition: color 0.18s; }
.email-trunc { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 160px; display: inline-block; }
.lo-val:hover { color: var(--neon); }
.re-val:hover { color: var(--a); }
.cyc-apply { display: block; margin: 0 14px 14px; text-align: center; text-decoration: none; font-family: 'Share Tech Mono', monospace; font-size: 10px; letter-spacing: 0.1em; padding: 10px; border-radius: 2px; border: 1px solid var(--neon); color: var(--neon); transition: background 0.2s, color 0.2s; }
.cyc-apply:hover { background: var(--neon); color: #0c0f0c; }

@media (max-width: 820px) {
  .cy-body { grid-template-columns: 1fr; padding: 24px; }
  .cy-header-inner { padding: 12px 24px; }
  .cy-ticker { display: none; }
}
</style>
